import type { ReactNode } from 'react';

import type { RelativeTimeOption } from '@/data/inventory/relativeTime';

/**
 * The choice not to filter by date, in whichever selector is offering it. Both
 * selectors and the stored key spell it the same way, so it lives in one place.
 */
export const ALL_TIME = 'all';

/** A preset span, or the choice not to filter by date at all. */
export type TimeFilterValue = RelativeTimeOption | typeof ALL_TIME;

/**
 * A date property the filter can select, labelled as the sentence fragment that
 * follows the title ("Show jobs" + "that started...").
 */
export interface DateFilterProperty {
  label: string;
  value: string;
}

/**
 * Everything the date filter needs beyond the conditions themselves. The
 * relative key is which preset is selected, and it cannot be recovered from the
 * conditions: the preset resolves to an absolute timestamp when picked, so a
 * reload has nothing left to distinguish "last 24 hours" from any other span.
 * Whoever persists the conditions therefore has to persist this alongside them.
 */
export interface DateFilterConfig {
  title: string;
  properties: DateFilterProperty[];
  /** Sits ahead of the title. */
  icon?: ReactNode;
  /**
   * Drops the property selector, for a filter over one fixed property where that
   * selector would offer a single choice. Three things follow from it: the sole
   * remaining dropdown has to be where the filter is turned off, so "all time"
   * joins the presets; and the title carries the preposition itself ("Show
   * snapshots from"), so the presets shed the "in " their own labels start with.
   */
  fixedProperty?: boolean;
  relativeKey: string | undefined;
  onRelativeKeyChange: (relativeKey: string | undefined) => void;
}
